import React, { useState } from 'react';
import { X, RefreshCw, Wifi, ExternalLink, Check } from 'lucide-react';

interface PiConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceName: string;
  deviceId: number;
  piConnectUrl: string | undefined;
  onRequestPiConnect?: (deviceId: number) => Promise<void>;
  onRefreshDevice?: (deviceId: number) => Promise<{ piConnectUrl?: string }>;
}

const PiConnectModal: React.FC<PiConnectModalProps> = ({ 
  isOpen, 
  onClose, 
  deviceName,
  deviceId,
  piConnectUrl,
  onRequestPiConnect,
  onRefreshDevice
}) => {
  const [currentUrl, setCurrentUrl] = useState(piConnectUrl || '');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  if (!isOpen) return null;

  const handleRefresh = async () => {
    if (!onRefreshDevice) return;
    
    setIsRefreshing(true);
    try {
      const updatedDevice = await onRefreshDevice(deviceId);
      setCurrentUrl(updatedDevice.piConnectUrl || '');
    } catch (error) {
      console.error('Failed to refresh device:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRequestPiConnect = async () => {
    if (!onRequestPiConnect) return;
    
    setIsRequesting(true);
    try {
      await onRequestPiConnect(deviceId);
      // Optionally refresh after request
      if (onRefreshDevice) {
        setTimeout(async () => {
          const updatedDevice = await onRefreshDevice(deviceId);
          setCurrentUrl(updatedDevice.piConnectUrl || '');
        }, 2000); // Wait 2 seconds then refresh
      }
    } catch (error) {
      console.error('Failed to request Pi Connect:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleOk = () => {
    if (currentUrl && !currentUrl.startsWith('PENDING') && !currentUrl.startsWith('NOT_CONFIGURED')) {
      // Open URL in new tab
      window.open(currentUrl, '_blank', 'noopener,noreferrer');
    }
    onClose();
  };

  const isPending = currentUrl?.startsWith('PENDING') || false;
  const isNotConfigured = currentUrl?.startsWith('NOT_CONFIGURED') || currentUrl?.startsWith('TBA-') || !currentUrl;
  const hasValidUrl = Boolean(currentUrl && !isPending && !isNotConfigured && currentUrl.startsWith('https://connect.raspberrypi.com'));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium flex items-center">
            <Wifi className="w-5 h-5 mr-2 text-green-600" />
            Pi Connect URL - {deviceName}
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          {isPending ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="text-sm text-yellow-800">
                This device is pending activation. The Pi Connect URL will be available once the device completes its initial setup.
              </p>
            </div>
          ) : isNotConfigured ? (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
              <p className="text-sm text-gray-600">
                Pi Connect is not currently configured for this device. Click "Request Pi Connect" to generate a verification URL.
              </p>
            </div>
          ) : hasValidUrl ? (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800 mb-3">
                Pi Connect verification URL is ready! Click OK to open it in a new tab.
              </p>
              <p className="text-sm text-gray-600 mb-2">Device verification URL:</p>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={currentUrl}
                  readOnly
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-mono"
                  onClick={(e) => e.currentTarget.select()}
                />
                <button
                  onClick={() => navigator.clipboard.writeText(currentUrl)}
                  className="px-3 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm"
                  title="Copy to clipboard"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Click OK to open this URL in a new tab for device verification.
              </p>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
              <p className="text-sm text-gray-600">
                No Pi Connect URL available for this device.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-between">
          {/* Left side buttons */}
          <div className="flex space-x-3">
           {onRequestPiConnect && (
            <button
              onClick={handleRequestPiConnect}
              disabled={isRequesting}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRequesting ? 'Requesting...' : 'Request Pi Connect'}
            </button>
          )}
            
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Reloading...' : 'Reload'}
            </button>
          </div>

          {/* Right side buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleOk}
              disabled={!hasValidUrl}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PiConnectModal;